import { useEffect } from 'react';
import uPlot from 'uplot';
import { useAppStore } from '../state/store';
import type { ChartId } from '../state/store';

/**
 * Syncs the crosshair vertical line on a chart that is not currently hovered.
 * The hovered chart is driven by the pan plugin (mouse position); other charts
 * position the line at chartCursorFraction * rect.width so they stay aligned.
 */
export function useSyncedCrosshair(
  chartId: ChartId,
  chartRef: React.MutableRefObject<uPlot | null>,
  timestampsRef: React.MutableRefObject<number[]>,
  getLegendIndex: (timestamp: number | null) => number | undefined
): void {
  const chartCursorFraction = useAppStore((s) => s.chartCursorFraction);
  const chartCursorTimestamp = useAppStore((s) => s.chartCursorTimestamp);
  const chartHoveredBy = useAppStore((s) => s.chartHoveredBy);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const timestamps = timestampsRef.current;
    if (chartHoveredBy === chartId) return;
    if (chartCursorFraction == null || timestamps.length === 0) {
      chart.setCursor({ left: -10, top: 0 }, false);
      chart.setLegend({ idx: undefined }, false);
      return;
    }
    const left = chartCursorFraction * chart.rect.width;
    const idx = getLegendIndex(chartCursorTimestamp);
    chart.setCursor({ left, top: -999 }, false);
    chart.setLegend({ idx: idx ?? undefined }, false);
  }, [chartId, chartCursorFraction, chartCursorTimestamp, chartHoveredBy]);
}
